import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CalendarClock } from 'lucide-react';
import { getLegalDeadlines } from '../../../api/legalActs';
import { LEGAL_DEADLINE_KIND_LABELS } from '../../../types/legalAct';
function dueLabel(d) {
    const date = new Date(d.due).toLocaleDateString('fr-FR');
    if (d.days_left < 0)
        return `En retard : à mettre en ligne au plus tard le ${date}`;
    if (d.days_left === 0)
        return 'À mettre en ligne aujourd\'hui';
    return `À mettre en ligne d'ici le ${date} (${d.days_left} jour${d.days_left > 1 ? 's' : ''})`;
}
const STATUS_STYLES = {
    overdue: 'border-red-200 bg-red-50 text-red-800',
    soon: 'border-amber-200 bg-amber-50 text-amber-800',
    upcoming: 'border-gray-200 bg-white text-gray-700',
};
/**
 * Listes des délibérations et procès-verbaux à mettre en ligne (une semaine après la
 * séance, ou après l'approbation du procès-verbal). Rien ne s'affiche s'il n'y en a pas.
 */
export function LegalDeadlinesBlock({ compact = false }) {
    const navigate = useNavigate();
    const { data } = useQuery({ queryKey: ['admin-legal-deadlines'], queryFn: getLegalDeadlines });
    if (!data || data.length === 0)
        return null;
    const shown = compact ? data.filter((d) => d.status !== 'upcoming') : data;
    if (shown.length === 0)
        return null;
    // Le brouillon s'ouvre s'il existe ; sinon, la liste se prépare depuis la séance
    // et le procès-verbal se crée comme un acte.
    const open = (d) => {
        if (d.draft_act_id)
            navigate(`/commune/legal-acts/${d.draft_act_id}/edit`);
        else if (d.kind === 'deliberations_list')
            navigate(`/commune/council-sessions?session=${d.session.id}`);
        else
            navigate('/commune/legal-acts/new?type=pv_seance');
    };
    return (_jsxs("div", { className: "bg-white rounded-lg border border-gray-200 p-4 mb-4 space-y-2", children: [_jsxs("h2", { className: "text-sm font-semibold text-gray-700 flex items-center gap-2", children: [_jsx(CalendarClock, { size: 16 }), " Documents de s\u00E9ance \u00E0 mettre en ligne"] }), _jsx("ul", { className: "space-y-1.5", children: shown.map((d) => (_jsx("li", { children: _jsxs("button", { onClick: () => open(d), className: `w-full text-left text-sm px-3 py-2 rounded-lg border hover:underline ${STATUS_STYLES[d.status]}`, children: [_jsx("strong", { children: LEGAL_DEADLINE_KIND_LABELS[d.kind] }), " de la s\u00E9ance du ", new Date(d.session.session_date).toLocaleDateString('fr-FR'), " : ", dueLabel(d), d.draft_act_id ? ' · brouillon prêt' : ''] }) }, `${d.kind}-${d.session.id}`))) }), _jsx("p", { className: "text-xs text-gray-400", children: "La liste des d\u00E9lib\u00E9rations se met en ligne dans la semaine qui suit la s\u00E9ance, le proc\u00E8s-verbal dans la semaine qui suit la s\u00E9ance o\u00F9 il a \u00E9t\u00E9 arr\u00EAt\u00E9." })] }));
}
